import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Chip,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  FileDownload as FileDownloadIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import dayjs from 'dayjs';

const TransactionReport = ({ showSnackbar, setLoading, loading }) => {
  const [filters, setFilters] = useState({
    reportType: 'summary',
    dateFrom: dayjs().subtract(30, 'days'),
    dateTo: dayjs(),
    labType: '',
    paymentType: '',
    status: '',
  });

  const [reportData, setReportData] = useState({
    summary: {},
    chartData: [],
    detailedData: [],
  });

  useEffect(() => {
    generateReport();
  }, []);

  const generateReport = async () => {
    try {
      setLoading(true);

      // Simulate API call
      const mockSummary = {
        totalTransactions: 1245,
        totalRevenue: 87650000,
        successfulTransactions: 1156,
        failedTransactions: 89,
        averageTransactionAmount: 70361,
        topPaymentType: 'Assessment Fee',
      };

      const mockChartData = [
        { date: '2026-01-21', successful: 45, failed: 3, revenue: 3250000 },
        { date: '2026-01-22', successful: 52, failed: 2, revenue: 3680000 },
        { date: '2026-01-23', successful: 38, failed: 4, revenue: 2890000 },
        { date: '2026-01-24', successful: 61, failed: 1, revenue: 4320000 },
        { date: '2026-01-25', successful: 43, failed: 5, revenue: 3120000 },
        { date: '2026-01-26', successful: 55, failed: 2, revenue: 3890000 },
        { date: '2026-01-27', successful: 48, failed: 3, revenue: 3450000 },
      ];

      const mockDetailedData = [
        { type: 'Application Fee', count: 345, amount: 25670000, percentage: 29.3 },
        { type: 'Assessment Fee', count: 312, amount: 28930000, percentage: 33.0 },
        { type: 'Annual Fee', count: 234, amount: 18450000, percentage: 21.1 },
        { type: 'Certificate Fee', count: 189, amount: 9870000, percentage: 11.3 },
        { type: 'Others', count: 165, amount: 4730000, percentage: 5.4 },
      ];

      setReportData({
        summary: mockSummary,
        chartData: mockChartData,
        detailedData: mockDetailedData,
      });
    } catch (error) {
      showSnackbar('Error generating report', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleGenerateReport = () => {
    generateReport();
  };

  const handleExportReport = () => {
    // Simulate export functionality
    showSnackbar('Report export functionality will be implemented', 'info');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 2 }}>
        {/* Filter Section */}
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title="Transaction Report Filters"
            action={
              <Box>
                <Button
                  variant="outlined"
                  onClick={() => setFilters({
                    reportType: 'summary',
                    dateFrom: dayjs().subtract(30, 'days'),
                    dateTo: dayjs(),
                    labType: '',
                    paymentType: '',
                    status: '',
                  })}
                  sx={{ mr: 1 }}
                  startIcon={<RefreshIcon />}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  onClick={handleExportReport}
                  startIcon={<FileDownloadIcon />}
                >
                  Export Report
                </Button>
              </Box>
            }
          />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Report Type:</Typography>
                <RadioGroup
                  value={filters.reportType}
                  onChange={(e) => handleFilterChange('reportType', e.target.value)}
                  row
                >
                  <FormControlLabel value="summary" control={<Radio />} label="Summary Report" />
                  <FormControlLabel value="detailed" control={<Radio />} label="Detailed Report" />
                  <FormControlLabel value="comparison" control={<Radio />} label="Comparison Report" />
                </RadioGroup>
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Date From"
                  value={filters.dateFrom}
                  onChange={(newValue) => handleFilterChange('dateFrom', newValue)}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                  maxDate={dayjs()}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Date To"
                  value={filters.dateTo}
                  onChange={(newValue) => handleFilterChange('dateTo', newValue)}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                  maxDate={dayjs()}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Lab Type</InputLabel>
                  <Select
                    value={filters.labType}
                    label="Lab Type"
                    onChange={(e) => handleFilterChange('labType', e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="1">Testing</MenuItem>
                    <MenuItem value="2">Calibration</MenuItem>
                    <MenuItem value="3">Medical</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Payment Type</InputLabel>
                  <Select
                    value={filters.paymentType}
                    label="Payment Type"
                    onChange={(e) => handleFilterChange('paymentType', e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="1">Application Fee</MenuItem>
                    <MenuItem value="2">Assessment Fee</MenuItem>
                    <MenuItem value="3">Annual Fee</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Button
                  variant="contained"
                  onClick={handleGenerateReport}
                  startIcon={<SearchIcon />}
                  fullWidth
                  sx={{ height: '40px' }}
                >
                  Generate Report
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  {reportData.summary.totalTransactions?.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Transactions
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h5" fontWeight="bold" color="success.main">
                  {formatCurrency(reportData.summary.totalRevenue || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Revenue
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h5" fontWeight="bold" color="success.main">
                  {reportData.summary.successfulTransactions?.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Successful
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h5" fontWeight="bold" color="error.main">
                  {reportData.summary.failedTransactions?.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Failed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h5" fontWeight="bold" color="info.main">
                  {formatCurrency(reportData.summary.averageTransactionAmount || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Average Amount
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6" fontWeight="bold" color="secondary.main">
                  {reportData.summary.topPaymentType}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Top Payment Type
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Charts Section */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Daily Transaction Trend */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardHeader title="Daily Transaction Trend" />
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={reportData.chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(value) => dayjs(value).format('MM/DD')}
                    />
                    <YAxis yAxisId="left" orientation="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip 
                      labelFormatter={(value) => dayjs(value).format('MMM DD, YYYY')}
                      formatter={(value, name) => [
                        name === 'revenue' ? formatCurrency(value) : value,
                        name === 'successful' ? 'Successful' : 
                        name === 'failed' ? 'Failed' : 'Revenue'
                      ]}
                    />
                    <Legend />
                    <Line 
                      yAxisId="left" 
                      type="monotone" 
                      dataKey="successful" 
                      stroke="#00C49F" 
                      strokeWidth={2}
                      name="successful"
                    />
                    <Line 
                      yAxisId="left" 
                      type="monotone" 
                      dataKey="failed" 
                      stroke="#FF8042" 
                      strokeWidth={2}
                      name="failed"
                    />
                    <Bar yAxisId="right" dataKey="revenue" fill="#0088FE" opacity={0.3} name="revenue" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Payment Type Distribution */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardHeader title="Revenue by Payment Type" />
              <CardContent>
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={reportData.detailedData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={120}
                      paddingAngle={5}
                      dataKey="percentage"
                      label={({ type, percentage }) => `${type}: ${percentage}%`}
                    >
                      {reportData.detailedData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name, props) => [
                        `${value}% (${formatCurrency(props.payload.amount)})`,
                        'Share'
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Detailed Breakdown Table */}
        <Card>
          <CardHeader title="Payment Type Breakdown" />
          <CardContent>
            <Grid container spacing={2}>
              {reportData.detailedData.map((item, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {item.type}
                      </Typography>
                      <Chip 
                        label={`${item.percentage}%`} 
                        color="primary" 
                        size="small" 
                      />
                    </Box>
                    <Typography variant="h6" color="primary" fontWeight="bold">
                      {formatCurrency(item.amount)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.count} transactions
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Avg: {formatCurrency(item.amount / item.count)}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </LocalizationProvider>
  );
};

export default TransactionReport;